// src/utils/labTestPayment.js
//
// 15 Jul 2026 — new. Orchestrates the "pay before Thyrocare order creation"
// flow: create a Razorpay order (server resolves the amount from the panel
// code, never trusts a client-supplied amount) → open Razorpay's standard
// Checkout modal (src/services/razorpay.js) → on success, verify the
// signature server-side and only THEN create the actual Thyrocare order.
//
// Shared by BOTH the chat ordering flow (via toolRouter.js) and the patient
// self-service page (PatientOrderLabTestPage.jsx) — previously neither had
// any payment step at all (paymentDetails.payType was hardcoded to POSTPAID
// either way). Deliberately uses the axios-based api.js client (not
// toolRouter.js's own raw-fetch helpers) even when called from toolRouter —
// nothing prevents mixing HTTP clients, and duplicating this orchestration
// logic in two styles would be a worse tradeoff than one extra import.
//
// Uses the standard Razorpay Checkout modal (confirmed with Deepu before
// building this) — NOT a custom-branded payment screen, and NOT embedded in
// any custom panel. Razorpay's own script manages that modal's overlay
// entirely; openRazorpayCheckout() is a plain async function that resolves
// with the payment result directly, so "communicating success back to chat"
// isn't a separate problem to solve — the calling code gets the result in
// the same call stack.
//
// 27 Jul 2026 — added payAndCreateLabTestOrderWithWallet below. Kept as a
// SEPARATE function rather than a paymentMode branch inside
// payAndCreateLabTestOrder — same reasoning already used throughout this
// codebase for keeping paid/unpaid or wallet/gateway paths as distinct
// functions: zero risk of changing the existing RAZORPAY_UPFRONT behaviour.

import { paymentAPI } from '../services/api';
import { openRazorpayCheckout } from '../services/razorpay';

/**
 * @param {object} params
 * @param {string} params.patientId
 * @param {string} [params.panelCode]    e.g. "T1" or "T2" — used when the
 *                                       caller already knows the Previu tier
 *                                       (self-service ordering page).
 * @param {string} [params.testCode]     raw Thyrocare SKU (e.g. "AAROGYAM 1.1")
 *                                       — used when only the SKU is known
 *                                       (chat ordering flow); resolved to a
 *                                       tier server-side. Provide EITHER
 *                                       panelCode OR testCode, not neither.
 * @param {string} [params.panelName]    shown on the Razorpay modal; falls
 *                                       back to the server's resolved name
 *                                       if omitted (see createLabTestPaymentOrder response)
 * @param {string} [params.patientName]
 * @param {string} [params.patientEmail]
 * @param {string} [params.patientPhone]
 * @param {object} params.orderPayload   the full CreateOrderRequest-shaped
 *                                       body (same shape already sent to
 *                                       /labtest/orders directly before this
 *                                       change) — caller builds this, this
 *                                       function only handles payment gating.
 * @param {string} [params.paymentMode]  defaults to 'RAZORPAY_UPFRONT' — the
 *                                       only mode implemented today. Exists
 *                                       as an explicit seam for later, when
 *                                       a patient's subscription plan might
 *                                       cover a panel with no Razorpay
 *                                       transaction at all (SUBSCRIPTION_COVERED,
 *                                       not yet implemented backend-side —
 *                                       see PayAndCreateOrderRequest.java).
 * @param {string} [params.countryCode]  ISO country code — defaults to "IN"
 *                                       server-side if omitted. Threaded
 *                                       through to PaymentGatewayResolver;
 *                                       only Razorpay/India is wired today,
 *                                       but the seam exists for when a
 *                                       second gateway is added for other
 *                                       countries.
 * @param {string} [params.subscriptionPlanId] reserved for future gateway
 *                                       routing rules; not used yet.
 * @returns {Promise<{success: true, order: object} |
 *                    {success: false, reason: 'payment_cancelled'|'payment_failed'|'order_creation_failed'|'create_order_failed', message?: string}>}
 */
export async function payAndCreateLabTestOrder({
  patientId, panelCode, testCode, panelName, patientName, patientEmail, patientPhone, orderPayload,
  paymentMode = 'RAZORPAY_UPFRONT', countryCode, subscriptionPlanId,
}) {
  // Step 1 — create the Razorpay order (server resolves the amount)
  let razorpayOrderId, amount, currency, resolvedPanelName;
  try {
    const createRes = await paymentAPI.createLabTestPaymentOrder({
      patientId, panelCode, testCode, countryCode, subscriptionPlanId,
    });
    razorpayOrderId   = createRes.data.razorpayOrderId;
    amount             = createRes.data.amount;
    currency           = createRes.data.currency;
    resolvedPanelName  = createRes.data.panelName;
  } catch (err) {
    return {
      success: false,
      reason: 'create_order_failed',
      message: err?.response?.data?.error || err.message || 'Could not start the payment.',
    };
  }

  // Step 2 — open Razorpay's standard Checkout modal
  let checkoutResult;
  try {
    checkoutResult = await openRazorpayCheckout({
      orderId: razorpayOrderId,
      amount,
      currency,
      name: 'Previu',
      description: panelName || resolvedPanelName || `${panelCode} Panel`,
      prefill: {
        name: patientName || '',
        email: patientEmail || '',
        contact: patientPhone || '',
      },
      themeColor: '#b8843a', // Previu gold, matches PREVIU_DESIGN_TOKENS.md
    });
  } catch (err) {
    // openRazorpayCheckout rejects on payment.failed
    return { success: false, reason: 'payment_failed', message: err.message };
  }

  if (!checkoutResult) {
    // User dismissed the modal without completing payment (resolves null,
    // not a rejection) — treat the same as a failure for retry purposes,
    // but with a distinct reason so the UI can word it appropriately.
    return { success: false, reason: 'payment_cancelled' };
  }

  // Step 3 — verify signature server-side, THEN create the Thyrocare order
  // NOTE for when SUBSCRIPTION_COVERED is actually built: that mode needs a
  // completely different early path — no Razorpay order (step 1), no
  // Checkout modal (step 2), just a coverage check then straight to this
  // step with an empty/absent razorpay* trio. Not built now since the
  // backend deliberately rejects that mode today (see
  // LabTestController.payAndCreateOrder()) — this comment exists so
  // whoever builds it later knows where the branch belongs.
  try {
    const orderRes = await paymentAPI.payAndCreateLabTestOrder({
      razorpayOrderId:   checkoutResult.razorpay_order_id,
      razorpayPaymentId: checkoutResult.razorpay_payment_id,
      razorpaySignature: checkoutResult.razorpay_signature,
      order: orderPayload,
      paymentMode,
      countryCode,
      subscriptionPlanId,
    });
    return { success: true, order: orderRes.data };
  } catch (err) {
    // Payment DID succeed at this point (Razorpay already confirmed it) —
    // this failure means either signature verification or the actual
    // Thyrocare call failed AFTER a real payment went through. This is the
    // one case that genuinely needs manual reconciliation, not just a
    // "click to retry" — the backend logs this loudly for exactly that
    // reason (see LabTestServiceImpl.createOrderWithPayment's Javadoc).
    return {
      success: false,
      reason: 'order_creation_failed',
      message: err?.response?.data?.message || err.message ||
        'Payment succeeded but the order could not be created. Please contact support — do not pay again.',
    };
  }
}

/**
 * 27 Jul 2026 — new. WALLET_BALANCE mode: asks the server how much of the
 * panel price the patient's wallet can cover, opens Razorpay Checkout ONLY
 * for the shortfall (skipped entirely if the wallet covers it all), then
 * completes the order using the wallet checkout intent instead of a raw
 * gateway signature. Same params/return shape as payAndCreateLabTestOrder
 * above (minus paymentMode, which is always 'WALLET_BALANCE' here).
 */
export async function payAndCreateLabTestOrderWithWallet({
  patientId, panelCode, testCode, panelName, patientName, patientEmail, patientPhone, orderPayload,
  countryCode, subscriptionPlanId,
}) {
  // Step 1 — resolve the wallet/gateway split (server-side, same panel price
  // resolution as the plain flow above — just also asks WalletService how
  // much of it the wallet can cover).
  let split;
  try {
    const createRes = await paymentAPI.createLabTestPaymentOrder({
      patientId, panelCode, testCode, countryCode, subscriptionPlanId,
      paymentMode: 'WALLET_BALANCE',
    });
    split = createRes.data;
  } catch (err) {
    return {
      success: false,
      reason: 'create_order_failed',
      message: err?.response?.data?.error || err.message || 'Could not start the payment.',
    };
  }

  // Step 2 — open Checkout ONLY if there's a gateway shortfall to cover.
  let gatewayPaymentId = null;
  let gatewaySignature = null;

  if (split.gatewayContributionMinorUnits > 0) {
    let checkoutResult;
    try {
      checkoutResult = await openRazorpayCheckout({
        orderId: split.razorpayOrderId,
        amount: split.gatewayContributionMinorUnits,
        currency: split.currency || 'INR',
        name: 'Previu',
        description: panelName || split.panelName || `${panelCode} Panel`,
        prefill: {
          name: patientName || '',
          email: patientEmail || '',
          contact: patientPhone || '',
        },
        themeColor: '#b8843a',
      });
    } catch (err) {
      return { success: false, reason: 'payment_failed', message: err.message };
    }

    if (!checkoutResult) {
      return { success: false, reason: 'payment_cancelled' };
    }
    gatewayPaymentId = checkoutResult.razorpay_payment_id;
    gatewaySignature = checkoutResult.razorpay_signature;
  }
  // else: wallet covered the full amount — no Checkout modal was ever shown,
  // proceed straight to completing the intent below.

  // Step 3 — complete the checkout intent (verifies gateway portion if any,
  // debits wallet portion if any, THEN creates the Thyrocare order).
  try {
    const orderRes = await paymentAPI.payAndCreateLabTestOrder({
      order: orderPayload,
      paymentMode: 'WALLET_BALANCE',
      walletCheckoutIntentId: split.walletCheckoutIntentId,
      razorpayPaymentId: gatewayPaymentId,
      razorpaySignature: gatewaySignature,
      countryCode,
      subscriptionPlanId,
    });
    return { success: true, order: orderRes.data };
  } catch (err) {
    return {
      success: false,
      reason: 'order_creation_failed',
      message: err?.response?.data?.message || err.message ||
        'Payment succeeded but the order could not be created. Please contact support — do not pay again.',
    };
  }
}
